import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Button,
  TextField,
  IconButton,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { io, Socket } from 'socket.io-client';
import { 
  getAiConversations, 
  getAiConversationAnalytics, 
  getConversationDetails, 
  getCollectionConversations, 
  getCollectionConversationAnalytics, 
  getCollectionConversationDetails,
  getAis
} from '../services/api';
import { UniversalConversation, ConversationAnalytics, UniversalMessage } from '../types';
import config from '../config';

const Review = () => {
  const { aiId, collectionId } = useParams<{ aiId: string; collectionId?: string }>();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<UniversalConversation[]>([]);
  const [analytics, setAnalytics] = useState<ConversationAnalytics | null>(null);
  const [expandedConversations, setExpandedConversations] = useState<{ [key: string]: UniversalMessage[] }>({});
  const [conversationPreviews, setConversationPreviews] = useState<{ [key: string]: UniversalMessage[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versionInfo, setVersionInfo] = useState<{ version: string; collectionName: string } | null>(null);

  // Manual messaging state
  const [manualMessages, setManualMessages] = useState<{ [key: string]: string }>({});
  const [sendingMessages, setSendingMessages] = useState<{ [key: string]: boolean }>({});
  const socketRef = useRef<Socket | null>(null);

  // Helper function to truncate annotation to 2 lines (approximately 120-140 characters)
  // const truncateAnnotation = (annotation: string, maxLength: number = 140) => {
  //   if (!annotation) return '';

  //   // Remove leading/trailing whitespace and normalize line breaks
  //   const cleanedAnnotation = annotation.trim().replace(/\s+/g, ' ');

  //   if (cleanedAnnotation.length <= maxLength) return cleanedAnnotation;

  //   // Find a good truncation point (preferably at a word boundary)
  //   let truncated = cleanedAnnotation.substring(0, maxLength);
  //   const lastSpaceIndex = truncated.lastIndexOf(' ');

  //   if (lastSpaceIndex > maxLength * 0.7) { // If we can find a space in the last 30%
  //     truncated = truncated.substring(0, lastSpaceIndex);
  //   }

  //   return truncated + '...';
  // };

  // Define loadConversationDetails before Socket.io setup since it's referenced in socket events
  const loadConversationDetails = useCallback(async (conversationId: string) => {
    if (!aiId || !conversationId || expandedConversations[conversationId]) return;

    try {
      let response;
      if (collectionId) {
        response = await getCollectionConversationDetails(aiId, collectionId, conversationId);
      } else {
        response = await getConversationDetails(aiId, conversationId);
      }
      const conversationData = response.data.data || response.data;
      setExpandedConversations(prev => ({
        ...prev,
        [conversationId]: conversationData.messages || []
      }));

      // Join the conversation room for real-time updates using Supabase conversation ID
      if (socketRef.current?.connected) {
        socketRef.current.emit('join_conversation', {
          conversationId,
          userType: 'trainer',
          trainerId: localStorage.getItem('userEmail') || 'unknown'
        });
        console.log(`🔌 Joined conversation room for ${conversationId}`);
      }
    } catch (err) {
      console.error('Error loading conversation details:', err);
      setExpandedConversations(prev => ({
        ...prev,
        [conversationId]: []
      }));
    }
  }, [aiId, collectionId, expandedConversations]);

  // Socket.io connection setup
  useEffect(() => {
    const socketUrl = config.socketUrl;
    console.log(`🔌 REVIEW SOCKET: Connecting to ${socketUrl}`);
    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('🔌 Socket connected to review page:', socket.id);
      // Join conversation rooms for expanded conversations
      Object.keys(expandedConversations).forEach(conversationId => {
        socket.emit('join_conversation', {
          conversationId,
          userType: 'trainer',
          trainerId: localStorage.getItem('userEmail') || 'unknown'
        });
      });
    });

    socket.on('new_message', (data) => {
      console.log('📨 New message received in review:', data);
      // Refresh the conversation details for this conversation
      if (data.conversationId && expandedConversations[data.conversationId]) {
        loadConversationDetails(data.conversationId);
      }
    });

    socket.on('manual_response', (data) => {
      console.log('📨 Manual response received in review:', data);
      // Refresh the conversation details for this conversation
      if (data.conversationId && expandedConversations[data.conversationId]) {
        loadConversationDetails(data.conversationId);
      }
    });

    socket.on('live_user_message', (data) => {
      console.log('📨 Live user message received in review:', data);
      // Refresh the conversation details for this conversation to show new user message
      if (data.conversationId && expandedConversations[data.conversationId]) {
        loadConversationDetails(data.conversationId);
      }
    });

    socket.on('disconnect', () => {
      console.log('🔌 Socket disconnected from review page');
    });

    return () => {
      socket.disconnect();
    };
  }, [expandedConversations, loadConversationDetails]);

  // Send manual message function
  const sendManualMessage = async (conversationId: string) => {
    const message = manualMessages[conversationId]?.trim();
    if (!message) return;

    setSendingMessages(prev => ({ ...prev, [conversationId]: true }));

    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '');
      const endpoint = `${apiUrl}/manual/conversations/${conversationId}/manual-response`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ message })
      });

      if (response.ok) {
        // Clear the message input
        setManualMessages(prev => ({ ...prev, [conversationId]: '' }));
        // Refresh conversation details
        loadConversationDetails(conversationId);
        console.log('✅ Manual message sent successfully from review page');
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to send manual message:', response.status, errorText);
        // TODO: Show error to user
      }
    } catch (error) {
      console.error('❌ Error sending manual message:', error);
      // TODO: Show error to user
    } finally {
      setSendingMessages(prev => ({ ...prev, [conversationId]: false }));
    }
  };

  // Define callback functions before they are used in useEffect
  const loadConversationPreview = useCallback(async (conversationId: string) => {
    if (!aiId || !conversationId || conversationPreviews[conversationId]) return;

    try {
      let response;
      if (collectionId) {
        response = await getCollectionConversationDetails(aiId, collectionId, conversationId);
      } else {
        response = await getConversationDetails(aiId, conversationId);
      }
      const conversationData = response.data.data || response.data;
      const messages = conversationData.messages || [];
      // Get last 4 messages for preview
      const preview = messages.slice(-4);

      setConversationPreviews(prev => ({
        ...prev,
        [conversationId]: preview
      }));
    } catch (error) {
      console.error('Error loading conversation preview:', error);
    }
  }, [aiId, collectionId, conversationPreviews]);

  const loadAnalytics = useCallback(async () => {
    if (!aiId) return;
    try {
      let response;
      if (collectionId) {
        response = await getCollectionConversationAnalytics(aiId, collectionId);
      } else {
        response = await getAiConversationAnalytics(aiId);
      }
      setAnalytics(response.data.data || response.data);
    } catch (err) {
      console.error('Error loading analytics:', err);
      // Set default analytics if API fails
      setAnalytics({
        total_conversations: 0,
        total_messages: 0,
        unique_users: 0,
        avg_messages_per_conversation: 0,
        recent_activity: []
      });
    }
  }, [aiId, collectionId]);

  const loadConversations = useCallback(async () => {
    if (!aiId) return;
    try {
      let response;
      if (collectionId) {
        response = await getCollectionConversations(aiId, collectionId, 20); // Get 20 most recent conversations
      } else {
        response = await getAiConversations(aiId, 20);
      }
      const conversationsData = response.data.data || response.data || [];
      setConversations(conversationsData);

      // Load previews for all conversations
      conversationsData.forEach((conversation: UniversalConversation) => {
        if (conversation.conversation_id) {
          loadConversationPreview(conversation.conversation_id);
        }
      });
    } catch (err) {
      console.error('Error loading conversations:', err);
      setConversations([]);
    }
  }, [aiId, collectionId, loadConversationPreview]);

  const redirectToDefaultVersion = useCallback(async () => {
    if (!aiId) return;
    try {
      const response = await getAis();
      const ais = response.data;
      const targetAi = ais.find((ai: any) => ai.id === aiId);
      
      if (targetAi && targetAi.collections && targetAi.collections.length > 0) {
        // Find the most recent public collection
        const publicCollections = targetAi.collections.filter((c: any) => c.isPublic);
        const defaultCollection = publicCollections.length > 0 
          ? publicCollections[publicCollections.length - 1]  // Most recent public
          : targetAi.collections[targetAi.collections.length - 1]; // Most recent overall
        
        navigate(`/review/${aiId}/versions/${defaultCollection._id}`, { replace: true });
      } else {
        setError('No versions found for this AI');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading AI info:', err);
      setError('Failed to load AI information');
      setLoading(false);
    }
  }, [aiId, navigate]);

  const loadVersionInfo = useCallback(async () => {
    if (!aiId || !collectionId) return;
    try {
      const response = await getAis();
      const ais = response.data;
      const ai = ais.find((a: any) => a.id === aiId);
      if (ai?.collections) {
        const collection = ai.collections.find((c: any) => c._id === collectionId);
        if (collection) {
          setVersionInfo({
            version: collection.version || 'Unknown Version',
            collectionName: collection.collectionName
          });
        }
      }
    } catch (err) {
      console.error('Error loading version info:', err);
    }
  }, [aiId, collectionId]);

  useEffect(() => {
    if (!aiId) return;

    const initializeData = async () => {
      try {
        // If no collectionId provided (legacy route), try to redirect to default version
        if (!collectionId) {
          await redirectToDefaultVersion();
          return;
        }

        // Load version info first, then data
        await loadVersionInfo();
        await Promise.all([
          loadAnalytics(),
          loadConversations()
        ]);
        setLoading(false);
      } catch (err) {
        console.error('Error loading review data:', err);
        setError('Failed to load review data');
        setLoading(false);
      }
    };

    initializeData();
  }, [aiId, collectionId, loadAnalytics, loadConversations, loadVersionInfo, redirectToDefaultVersion]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden', p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Review & Analytics
      </Typography>
      
      {/* Version Information */}
      {versionInfo && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Version: {versionInfo.version}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Collection: {versionInfo.collectionName}
          </Typography>
        </Box>
      )}
      
      {/* Analytics Cards */}
      {analytics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Messages
                </Typography>
                <Typography variant="h4">
                  {analytics.total_messages || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Conversations
                </Typography>
                <Typography variant="h4">
                  {analytics.total_conversations || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Unique Users
                </Typography>
                <Typography variant="h4">
                  {analytics.unique_users || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Avg Messages/Conv
                </Typography>
                <Typography variant="h4">
                  {(analytics.avg_messages_per_conversation || 0).toFixed(1)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Recent Conversations */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Recent Conversations
      </Typography>
      
      {conversations.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No conversations found for this AI yet.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
          {conversations.map((conversation, index) => (
            <Accordion 
              key={conversation.conversation_id || `conversation-${index}`}
              sx={{ mb: 2, width: '100%', maxWidth: '100%', overflow: 'hidden' }}
              onChange={(_, isExpanded) => {
                if (isExpanded && conversation.conversation_id) {
                  loadConversationDetails(conversation.conversation_id);
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Typography variant="subtitle1" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      Conversation {conversation.conversation_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {conversation.started_at ? new Date(conversation.started_at).toLocaleString() : 'Unknown date'} • {conversation.total_messages} messages
                    </Typography>
                    
                    {/* Message Preview - Last 4 messages */}
                    {conversationPreviews[conversation.conversation_id] && conversationPreviews[conversation.conversation_id].length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                          Recent Messages:
                        </Typography>
                        {conversationPreviews[conversation.conversation_id].map((message, msgIndex) => (
                          <Box key={msgIndex} sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            {message.message_type === 'user' ? (
                              <PersonIcon sx={{ fontSize: 16, color: 'primary.main', mt: 0.2 }} />
                            ) : (
                              <SmartToyIcon sx={{ fontSize: 16, color: 'success.main', mt: 0.2 }} />
                            )}
                            <Typography variant="body2" sx={{
                              fontSize: '0.85rem',
                              color: 'text.secondary',
                              wordBreak: 'break-word',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {message.content?.substring(0, 120)}{message.content?.length > 120 ? '...' : ''}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent accordion from expanding
                        if (collectionId) {
                          navigate(`/review/${aiId}/versions/${collectionId}/conversation/${conversation.conversation_id}`);
                        } else {
                          navigate(`/review/${aiId}/conversation/${conversation.conversation_id}`);
                        }
                      }}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      View Full
                    </Button>
                    <Chip 
                      label={conversation.user_type} 
                      size="small" 
                      color={conversation.user_type === 'trainer' ? 'primary' : 'default'}
                    />
                    {conversation.last_message_at !== conversation.started_at && (
                      <Chip 
                        label={`Last: ${conversation.last_message_at ? new Date(conversation.last_message_at).toLocaleString() : 'Unknown'}`} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {expandedConversations[conversation.conversation_id] ? (
                  <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                    {/* Show only last 2 messages without context details */}
                    {expandedConversations[conversation.conversation_id].slice(-2).map((message, idx) => {
                      const isUser = message.message_type === 'user';
                      const isManual = message.search_results_metadata?.isManual === true;

                      return (
                        <Box
                          key={message.id || `message-${idx}`}
                          sx={{
                            mb: 2,
                            p: 2,
                            bgcolor: isUser ? '#f0f8ff' : (isManual ? '#fff8e1' : '#f9f9f9'),
                            borderRadius: 1,
                            borderLeft: `4px solid ${isUser ? '#1976d2' : (isManual ? '#ff9800' : '#4caf50')}`,
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            wordBreak: 'break-word'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isUser ? (
                              <PersonIcon sx={{ fontSize: 12 }} />
                            ) : isManual ? (
                              <PersonIcon sx={{ fontSize: 12 }} />
                            ) : (
                              <SmartToyIcon sx={{ fontSize: 12 }} />
                            )}
                            {isUser ? 'USER' : (isManual ? 'TRAINER' : 'AI')} • {message.created_at ? new Date(message.created_at).toLocaleString() : 'Unknown date'}
                            {isManual && (
                              <Chip
                                label="Manual"
                                size="small"
                                color="warning"
                                sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                              />
                            )}
                            {message.rating && (
                              <Chip
                                label={message.rating}
                                size="small"
                                color={message.rating === 'good' ? 'success' : 'error'}
                                sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                              />
                            )}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              mt: 1,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word'
                            }}
                          >
                            {message.content.length > 200 ? message.content.substring(0, 200) + '...' : message.content}
                          </Typography>
                        </Box>
                      );
                    })}
                    
                    {/* Manual Message Interface */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        Send Manual Response
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          maxRows={3}
                          placeholder="Type your response as the trainer..."
                          value={manualMessages[conversation.conversation_id] || ''}
                          onChange={(e) => setManualMessages(prev => ({
                            ...prev,
                            [conversation.conversation_id]: e.target.value
                          }))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendManualMessage(conversation.conversation_id);
                            }
                          }}
                          disabled={sendingMessages[conversation.conversation_id]}
                        />
                        <IconButton
                          color="primary"
                          onClick={() => sendManualMessage(conversation.conversation_id)}
                          disabled={!manualMessages[conversation.conversation_id]?.trim() || sendingMessages[conversation.conversation_id]}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {sendingMessages[conversation.conversation_id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SendIcon />
                          )}
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        This message will be sent directly to the visitor and appear as coming from you.
                      </Typography>
                    </Box>

                    {/* Show message count and View Full Conversation button */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {expandedConversations[conversation.conversation_id].length > 2 &&
                          `Showing last 2 of ${expandedConversations[conversation.conversation_id].length} messages`
                        }
                        {expandedConversations[conversation.conversation_id].length <= 2 &&
                          `${expandedConversations[conversation.conversation_id].length} messages total`
                        }
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (collectionId) {
                            navigate(`/review/${aiId}/versions/${collectionId}/conversation/${conversation.conversation_id}`);
                          } else {
                            navigate(`/review/${aiId}/conversation/${conversation.conversation_id}`);
                          }
                        }}
                        sx={{ mb: 1 }}
                      >
                        View Full Conversation
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      Loading conversation details...
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Review;
